import m from '@bitstillery/mithril'
import {classes, present, route_param_text, route_params_text} from '@bitstillery/common/lib/utils'
import {Button, Icon} from '@bitstillery/common/components'
import {MithrilComponent} from '@bitstillery/mithril'
import {$s, logger} from '@bitstillery/common/app'
import {match} from 'path-to-regexp'
import {nextTick} from '@bitstillery/mithril'

import type {RouteParamValue} from '@bitstillery/mithril'

/** Legacy `tabId` mode: the caller's state that holds the id of the shown tab. */
export interface TabsData {
    tabs: {active: RouteParamValue | undefined}
}

export interface TabsAttrs {
    /** An array of arbitrary <Tab> components */
    children?: m.Children
    /** Optional class name */
    className?: string
    /** Optional data object to sync the active tab (legacy; use route instead) */
    data?: TabsData
    /** This is used together with the nested view; e.g. tabs are styled as a sort of breadcrumb trail*/
    header?: {
        icon?: string
        onclick?: () => void
        title?: string
    }
    route?: string | string[]
    tab_id?: string
    /** `nested-view`: horizontal strip; `nested-view-vertical`: sidebar nav + scrollable panel */
    type?: 'default' | 'nested-view' | 'nested-view-vertical'
    /**
     * Match tab routes against this instead of the live `$s.env.uri`. For a tab strip that has to keep
     * its shape while the app navigates away from it: mithril holds the outgoing view on screen until
     * the next route resolves, by which time `$s.env.uri` has moved on and no tab would match — so the
     * panels would blank out on the way out (see the portal auth dialog).
     */
    uri?: string
}

export interface TabAttrs {
    className?: string
    count?: number | null | undefined
    /** Set by Tabs */
    data?: TabsData | undefined
    disabled?: boolean | number | string | null
    icon?: string
    /** Accent tone for the tab, e.g. 'brand'. Adds a `tab-<type>` styling hook (used by chrome tabs). */
    type?: string
    id?: string
    link?: string
    route?: string | string[]
    tabindex?: number
    tip?: string | (() => unknown)
    text?: string
    onactivate?: (tab: m.Vnode<TabAttrs>, on_init: boolean) => void
    /** Fires when this tab is no longer selected (another tab shown, route/query change, etc.). */
    onhide?: (tab: m.Vnode<TabAttrs>) => void
    /** Fires when this tab’s control receives focus (native focus on the tab button/link). */
    onfocus?: (e: FocusEvent) => void
    /** Set by Tabs parent */
    activate?: () => Promise<void>
    is_tab_active?: (tab: m.Vnode<TabAttrs>) => TabActive
}

type TabVnode = m.Vnode<TabAttrs>

type TabChild = TabVnode & {tabs?: Tabs}

/** Truthy when the tab is shown; a route-matched tab answers its route match. */
type TabActive = ReturnType<Tabs['is_tab_active']>

type TabPanelVnode = m.Vnode<TabPanelAttrs> & {is_tab_active?: (tab: TabVnode) => TabActive; tab?: TabVnode; tabs?: Tabs}

/**
 * Tabs' children are <Tab> vnodes, each holding its <TabPanel> as first child; a tab the caller renders
 * conditionally leaves a hole (false, null) among them. Vnode attrs are untyped, so this only tells a
 * component vnode apart from the holes and text.
 */
function is_tab_vnode(child: m.Children): child is TabChild {
    return typeof child === 'object' && child !== null && !Array.isArray(child) && child.attrs !== undefined
}

function is_panel_vnode(child: m.Children): child is TabPanelVnode {
    return typeof child === 'object' && child !== null && !Array.isArray(child) && child.attrs !== undefined
}

export class Tabs extends MithrilComponent<TabsAttrs> {
    data: TabsData | null | undefined = null
    tab_id: string = ''
    /** `TabsAttrs.uri`, kept here because the children call `is_tab_active` without attrs in hand. */
    private route_uri: string | undefined = undefined

    /**
     * Tab changes use history.replaceState (see activate_tab) so Mithril route attrs stay stale.
     * Discover patches replaceState to fire `locationchange`, and env keeps $s.env.qs in sync with
     * the real URL — prefer that over m.route.param for legacy tabId mode.
     */
    private legacy_tab_id_from_location(): string | undefined {
        if (!this.tab_id) {
            return undefined
        }
        const q = $s.env.qs[this.tab_id]
        if (q != null && q !== '') {
            return q
        }
        const param = route_param_text(m.route.param(this.tab_id))
        if (param != null && param !== '') {
            return param
        }
        return undefined
    }

    /** Path + query for tab-only replaceState; must match the visible URL, not m.route.get(). */
    private legacy_path_and_params(): {path: string; params: Record<string, string>} {
        const from_route = route_params_text(m.parsePathname(m.route.get()).params)
        const from_qs = $s.env.qs as Record<string, string>
        const params = {...from_route, ...from_qs}
        const path =
            typeof window !== 'undefined' && (window.location as Location | undefined)?.pathname
                ? window.location.pathname || '/'
                : m.parsePathname(m.route.get()).path
        return {path, params}
    }

    override oninit(vnode: m.Vnode<TabsAttrs>) {
        this.data = vnode.attrs.data
        this.route_uri = vnode.attrs.uri

        if (vnode.attrs.data) {
            this.tab_id = vnode.attrs.tab_id ? vnode.attrs.tab_id : 'tabId'
        }

        for (const tab of Array.isArray(vnode.children) ? vnode.children : []) {
            if (!is_tab_vnode(tab)) {
                continue
            }
            if (this.is_tab_active(tab)) {
                void this.activate_tab(tab, true)
            }
        }
    }

    override onbeforeupdate(vnode: m.Vnode<TabsAttrs>) {
        this.route_uri = vnode.attrs.uri
        // Sync data.tabs.active from URL when it changes (e.g. browser back/forward, external link)
        if (this.data?.tabs && this.tab_id) {
            const url_tab_id = this.legacy_tab_id_from_location()
            if (url_tab_id != null && url_tab_id !== this.data.tabs.active) {
                this.data.tabs.active = url_tab_id
            }
        }
    }

    async activate_tab(tab: TabVnode, on_init = false) {
        if (on_init) {
            if (tab.attrs.onactivate) {
                await nextTick()
                tab.attrs.onactivate(tab, on_init)
            }
            return
        }

        const {path, params: current_params} = this.legacy_path_and_params()
        if (tab.attrs.link && tab.attrs.route) {
            const link_path = m.parsePathname(tab.attrs.link)

            const match_route = match(tab.attrs.route)
            if (match_route(link_path.path)) {
                void m.route.set(link_path.path, current_params, {replace: true})
            }
        } else {
            if (current_params[this.tab_id] !== tab.attrs.id) {
                const new_params = {...current_params, [this.tab_id]: tab.attrs.id ?? ''}
                logger.debug(`[tabs] update tab id (${String(current_params[this.tab_id])} => ${String(tab.attrs.id)})`)
                // Use history.replaceState for tab-only changes to avoid full route resolution
                // (m.route.set triggers resolveRoute → mountRedraw.redraw() = full app re-render)
                const query = new URLSearchParams(new_params).toString()
                const new_path = query ? `${path}?${query}` : path
                history.replaceState(null, '', m.route.prefix + new_path)
                present(this.data, 'tabs data').tabs.active = tab.attrs.id
            }
        }

        if (tab.attrs.onactivate) {
            await nextTick()
            tab.attrs.onactivate(tab, on_init)
        }
    }

    is_tab_active(tab: TabVnode) {
        if (tab.attrs.route) {
            const uri = this.route_uri ?? $s.env.uri
            if (Array.isArray(tab.attrs.route)) {
                return tab.attrs.route.some((route) => {
                    const match_route = match(route)
                    return match_route(uri)
                })
            }
            const match_route = match(tab.attrs.route)
            return match_route(uri)
        }
        // Legacy mode using tabId: prefer live query string ($s.env.qs); m.route.param is stale
        // after tab-only history.replaceState (see activate_tab).
        const url_tab_id = this.legacy_tab_id_from_location()
        return tab.attrs.id === (url_tab_id ?? present(this.data, 'tabs data').tabs.active)
    }

    view(vnode: m.Vnode<TabsAttrs>) {
        return (
            <div className={classes('c-tabs', vnode.attrs.className, `type-${vnode.attrs.type ? vnode.attrs.type : 'default'}`)}>
                <div className='tabs-container'>
                    {vnode.attrs.header && (
                        <div
                            class='header'
                            onclick={() => {
                                const header = present(vnode.attrs.header, 'tabs header')
                                if (header.onclick) {
                                    header.onclick()
                                }
                            }}
                        >
                            {vnode.attrs.header.icon ? <Icon name={vnode.attrs.header.icon} type='unset' /> : null}
                            <span>{vnode.attrs.header.title}</span>
                        </div>
                    )}
                    <div class='tabs'>
                        {Array.isArray(vnode.children) &&
                            vnode.children.map((tab) => {
                                if (!is_tab_vnode(tab)) {
                                    return null
                                }
                                tab.attrs.data = vnode.attrs.data
                                tab.attrs.activate = this.activate_tab.bind(this, tab, false)
                                tab.attrs.is_tab_active = this.is_tab_active.bind(this, tab)
                                tab.tabs = this
                                return tab
                            })}
                    </div>
                </div>
                <div class='panels'>
                    {Array.isArray(vnode.children) &&
                        vnode.children.filter(is_tab_vnode).map((tab) => {
                            const first_child = Array.isArray(tab.children) ? tab.children[0] : undefined
                            const panel = present(is_panel_vnode(first_child) ? first_child : undefined, 'tab panel')
                            panel.attrs.id = tab.attrs.id
                            panel.attrs.data = this.data
                            panel.attrs.route = vnode.attrs.route
                            panel.is_tab_active = this.is_tab_active.bind(this, tab)
                            panel.tabs = this
                            panel.tab = tab
                            return panel
                        })}
                </div>
            </div>
        )
    }
}

export class Tab extends MithrilComponent<TabAttrs> {
    private tab_was_active: TabActive = false

    override oninit(vnode: TabVnode) {
        if (vnode.attrs.is_tab_active) {
            this.tab_was_active = vnode.attrs.is_tab_active(vnode)
        }
    }

    override onbeforeupdate(vnode: TabVnode) {
        const active = vnode.attrs.is_tab_active ? vnode.attrs.is_tab_active(vnode) : false
        if (this.tab_was_active && !active && vnode.attrs.onhide) {
            vnode.attrs.onhide(vnode)
        }
        this.tab_was_active = active
    }

    // Tabs sets `tabs` on each tab vnode it renders.
    view(vnode: TabVnode & {tabs: Tabs}) {
        return (
            <Button
                className={classes('c-tab', vnode.attrs.className, vnode.attrs.type ? `tab-${vnode.attrs.type}` : '', {
                    active: vnode.tabs.is_tab_active(vnode),
                })}
                disabled={vnode.attrs.disabled}
                icon={vnode.attrs.icon}
                onclick={vnode.attrs.activate}
                onfocus={vnode.attrs.onfocus}
                text={vnode.attrs.text}
                tabindex={vnode.attrs.tabindex}
                tip={vnode.attrs.tip}
                type='unset'
                variant='unset'
            >
                {typeof vnode.attrs.count === 'number' && <span class='count'>({vnode.attrs.count})</span>}
            </Button>
        )
    }
}

interface TabPanelAttrs {
    className?: string
    /** Set by Tabs */
    data?: TabsData | null | undefined
    id?: string | undefined
    route?: string | string[] | undefined
}

export class TabPanel extends MithrilComponent<TabPanelAttrs> {
    initialized = false

    // Tabs sets these on each panel vnode it renders.
    view(vnode: m.Vnode<TabPanelAttrs> & {is_tab_active: (tab: TabVnode) => TabActive; tab: TabVnode; tabs: Tabs}) {
        return (
            <div
                className={classes('c-tab-panel', vnode.attrs.className, {
                    active: vnode.tabs.is_tab_active(vnode.tab),
                })}
            >
                {(() => {
                    if (!this.initialized) {
                        // Initialize the tab content on the first time it is activated.

                        if (!vnode.is_tab_active(vnode.tab)) {
                            return null
                        }
                        this.initialized = true
                    }
                    return vnode.children
                })()}
            </div>
        )
    }
}
